import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import { fetchStudentEnrollments, createStudentEnrollment } from "@/api/students";
import { fetchGroups } from "@/api/groups";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { ENROLLMENT_STATUS_LABELS } from "@/types/enums";
import { extractErrorMessage } from "@/types/common";
import { formatDate, formatMoney } from "@/utils/format";
import { useAuth } from "@/auth/useAuth";

export function StudentEnrollmentsTab({ studentId }: { studentId: string }) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("students.edit");
  const canSeeFinance = hasPermission("fin.debts");
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupId, setGroupId] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: enrollments, isLoading, error } = useQuery({
    queryKey: ["student-enrollments", studentId],
    queryFn: () => fetchStudentEnrollments(studentId),
  });

  const { data: groups } = useQuery({
    queryKey: ["groups", "active-for-enroll"],
    queryFn: () => fetchGroups({ status: "ACTIVE", limit: 200 }),
    enabled: dialogOpen,
  });

  const mutation = useMutation({
    mutationFn: () => createStudentEnrollment(studentId, { studentId, groupId, startDate }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["student-enrollments", studentId] });
      setDialogOpen(false);
      setGroupId("");
    },
    onError: (err) => setServerError(extractErrorMessage(err)),
  });

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        {canEdit && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Записать в группу
          </Button>
        )}
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Группа</TableCell>
              <TableCell>Предмет</TableCell>
              <TableCell>Уровень</TableCell>
              <TableCell>Преподаватель</TableCell>
              <TableCell>Дата начала</TableCell>
              {canSeeFinance && <TableCell>Скидка</TableCell>}
              <TableCell>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(enrollments ?? []).map((e) => (
              <TableRow key={e.id}>
                <TableCell>{e.groupCode ?? e.groupId}</TableCell>
                <TableCell>{e.subjectName ?? "—"}</TableCell>
                <TableCell>{e.level ?? "—"}</TableCell>
                <TableCell>{e.teacherName ?? "—"}</TableCell>
                <TableCell>{formatDate(e.startDate)}</TableCell>
                {canSeeFinance && <TableCell>{e.discount ? formatMoney(e.discount) : "—"}</TableCell>}
                <TableCell>
                  <StatusChip label={ENROLLMENT_STATUS_LABELS[e.status]} />
                </TableCell>
              </TableRow>
            ))}
            {(enrollments ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={canSeeFinance ? 7 : 6} align="center">
                  Ученик пока не зачислен ни в одну группу.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Записать в группу</DialogTitle>
        <DialogContent>
          {serverError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverError}
            </Alert>
          )}
          <Stack spacing={2} mt={1}>
            <TextField select label="Группа" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {(groups?.items ?? []).map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.code} — {g.subjectName ?? g.subjectId} ({g.timeSlot})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Дата начала"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Отмена
          </Button>
          <Button variant="contained" disabled={!groupId || mutation.isPending} onClick={() => mutation.mutate()}>
            Записать
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
